import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Divider from '@mui/material/Divider'
import IconButton from '@mui/material/IconButton'
import LinearProgress from '@mui/material/LinearProgress'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Tab from '@mui/material/Tab'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tabs from '@mui/material/Tabs'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { useState, type ReactNode } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { describeError } from '../api/client'
import {
  labourApi,
  materialReceiptApi,
  siteIssueApi,
  useCompleteness,
  useContractors,
  useLabourBulk,
  useDailyReport,
  useIssueCategories,
  useReportAction,
  useReportHistory,

  type IssuePriority,
  type LabourEntry,
  type MaterialReceipt,
  type SiteIssue,
} from '../api/execution'
import { useLabourCategories } from '../api/hooks'
import { useAuth } from '../auth/useAuth'
import { ErrorBlock, LoadingBlock } from '../components/Feedback'
import { ActualAgainstPlan, ScopeChip } from '../components/StatusChip'
import { TemplateBar } from '../components/TemplateBar'
import {
  IssueStatusChip,
  PriorityChip,
  ReportStatusChip,
} from '../components/ReportStatusChip'
import { DrawingsTab } from './report/DrawingsTab'
import { LabourEditForm } from './report/LabourEditForm'
import { LabourQuickForm } from './report/LabourQuickForm'
import { MaterialMultiForm } from './report/MaterialMultiForm'
import { PhotosTab } from './report/PhotosTab'
import { PlanningTab } from './report/PlanningTab'
import { WorkTab } from './report/WorkTab'

export function DailyReportDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const { can } = useAuth()
  const [tab, setTab] = useState(0)

  const report = useDailyReport(id)
  const completeness = useCompleteness(id)
  const action = useReportAction(id)
  const [rejectOpen, setRejectOpen] = useState(false)
  const [notice, setNotice] = useState<{ severity: 'success' | 'warning'; text: string } | null>(null)

  if (report.isLoading) return <LoadingBlock label="Loading report..." />
  if (report.error) return <ErrorBlock error={report.error} />
  if (!report.data) return null

  const r = report.data
  const editable = r.editable

  async function run(actionName: string, body?: Record<string, unknown>) {
    setNotice(null)
    try {
      const result = await action.mutateAsync({ action: actionName, body })
      if (result?.warnings?.length) {
        setNotice({
          severity: 'warning',
          text: `Submitted with ${result.warnings.length} warning(s): ${result.warnings
            .map((w) => w.message)
            .join('; ')}`,
        })
      } else {
        setNotice({ severity: 'success', text: `Report ${actionName}d.` })
      }
    } catch (err) {
      setNotice({ severity: 'warning', text: describeError(err) })
    }
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pt: 3, pb: 1 }}>
        <IconButton size="small" onClick={() => navigate('/daily-reports')}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5" sx={{ fontFamily: 'monospace' }}>
          {r.reportNumber}
        </Typography>
        <ReportStatusChip status={r.status} size="medium" />
        {r.revisionNo > 0 && (
          <Typography variant="caption" color="text.secondary">
            revision {r.revisionNo}
          </Typography>
        )}
        <Box sx={{ flex: 1 }} />
        {editable && can('DAILY_REPORT:SUBMIT') && (
          <Button
            variant="contained"
            disabled={action.isPending}
            onClick={() => run('submit')}
          >
            Submit for review
          </Button>
        )}
        {r.status === 'SUBMITTED' || r.status === 'UNDER_REVIEW' ? (
          <>
            {can('DAILY_REPORT:APPROVE') && (
              <Button color="success" variant="contained" disabled={action.isPending}
                      onClick={() => run('approve')}>
                Approve
              </Button>
            )}
            {can('DAILY_REPORT:REJECT') && (
              <Button color="error" variant="outlined" onClick={() => setRejectOpen(true)}>
                Reject
              </Button>
            )}
          </>
        ) : null}
      </Box>

      {notice && (
        <Alert severity={notice.severity} sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice.text}
        </Alert>
      )}

      {r.status === 'REJECTED' && r.rejectionReason && (
        <Alert severity="error" sx={{ mb: 2 }}>
          <AlertTitle>Returned for correction</AlertTitle>
          {r.rejectionReason}
        </Alert>
      )}

      {!editable && (
        <Alert severity="info" sx={{ mb: 2 }}>
          This report is {r.status.toLowerCase().replace('_', ' ')} and is read-only. Entries can be
          changed again only if a reviewer reopens it.
        </Alert>
      )}

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, mb: 2 }}>
        <Card variant="outlined">
          <CardContent>
            <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))' }}>
              <Field label="Date" value={formatDate(r.reportDate)} />
              <Field label="Shift" value={r.shift} />
              <Field label="Site" value={`${r.siteCode ?? ''} ${r.siteName ?? ''}`.trim()} />
              <Field label="Project" value={r.projectName} />
              <Field label="Engineer" value={r.siteEngineerName} />
              <Field label="Weather" value={r.weather?.replace('_', ' ').toLowerCase()} />
              <Field label="Rain hours" value={r.rainHours != null ? String(r.rainHours) : '-'} />
              <Field
                label="Geofence"
                value={r.geofenceOk == null ? 'not checked' : r.geofenceOk ? 'inside' : 'OUTSIDE'}
              />
            </Box>
            {r.todayActualWork && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="caption" color="text.secondary">
                  Work actually done
                </Typography>
                <Typography variant="body2">{r.todayActualWork}</Typography>
              </>
            )}
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle2" gutterBottom>
              Completeness
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1.5 }}>
              <LinearProgress
                variant="determinate"
                value={completeness.data?.score ?? 0}
                sx={{ flex: 1, height: 8, borderRadius: 4 }}
              />
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {completeness.data?.score ?? 0}%
              </Typography>
            </Box>
            {(completeness.data?.sections ?? []).map((s) => (
              <Box key={s.key} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.25 }}>
                <Typography variant="body2" color={s.complete ? 'text.primary' : 'text.secondary'}>
                  {s.complete ? '✓' : '○'} {s.label}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {s.count}
                </Typography>
              </Box>
            ))}
          </CardContent>
        </Card>
      </Box>

      <Paper variant="outlined">
        <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto">
          <Tab label={`Work completed (${r.counts.workProgress})`} />
          <Tab label={`Labour (${r.counts.labourEntries})`} />
          <Tab label={`Material received (${r.counts.materialReceipts})`} />
          <Tab label="Drawings received" />
          <Tab label="Photos" />
          <Tab label={`Site issues (${r.counts.issues})`} />
          <Tab label="Tomorrow's plan" />
          <Tab label="History" />
        </Tabs>
        <Box sx={{ p: 2 }}>
          {tab === 0 && <WorkTab reportId={id} editable={editable} />}
          {tab === 1 && <LabourTab reportId={id} editable={editable} />}
          {tab === 2 && <MaterialTab reportId={id} editable={editable} />}
          {tab === 3 && <DrawingsTab reportId={id} editable={editable} />}
          {tab === 4 && <PhotosTab reportId={id} editable={editable} />}
          {tab === 5 && <IssuesTab reportId={id} siteId={r.siteId} editable={editable} />}
          {tab === 6 && <PlanningTab reportId={id} editable={editable} />}
          {tab === 7 && <HistoryTab reportId={id} />}
        </Box>
      </Paper>

      <RejectDialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        onReject={async (reason) => {
          await run('reject', { reason })
          setRejectOpen(false)
        }}
      />
    </>
  )
}


// ----------------------------------------------------------- labour tab

function LabourTab({ reportId, editable }: { reportId: string; editable: boolean }) {
  const list = labourApi.useList(reportId)
  const update = labourApi.useUpdate(reportId)
  const remove = labourApi.useDelete(reportId)
  const bulk = useLabourBulk(reportId)
  const categories = useLabourCategories()
  const contractors = useContractors()
  const [open, setOpen] = useState(false)
  const [editingRow, setEditingRow] = useState<LabourEntry | null>(null)
  const [confirmRow, setConfirmRow] = useState<LabourEntry | null>(null)

  const totals = (list.data ?? []).reduce(
    (acc, l: LabourEntry) => ({
      workers: acc.workers + (l.totalWorkers ?? 0),
      own: acc.own + (l.labourScope === 'OWN' ? (l.totalWorkers ?? 0) : 0),
      manHours: acc.manHours + Number(l.manHours ?? 0),
    }),
    { workers: 0, own: 0, manHours: 0 },
  )

  return (
    <>
      <TemplateBar module="LABOUR" reportId={reportId} editable={editable} />
      <Toolbar
        title={`Labour on site today — ${totals.workers} workers (${totals.own} own, ${
          totals.workers - totals.own
        } contractor), ${totals.manHours.toFixed(1)} man-hours.`}
        action={
          editable && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              Add labour
            </Button>
          )
        }
      />
      {bulk.error && <ErrorBlock error={bulk.error} title="Could not save the labour" />}
      {update.error && <ErrorBlock error={update.error} title="Could not save the change" />}
      {remove.error && <ErrorBlock error={remove.error} title="Could not remove the entry" />}
      <DataTable
        columns={['Category', 'Whose labour', 'Planned', 'Male', 'Female', 'Skilled', 'Semi', 'Unskilled', 'Total', 'Hours', 'OT', 'Man-hours', '']}
        rows={(list.data ?? []).map((l: LabourEntry) => [
          l.labourCategoryName,
          <ScopeChip scope={l.labourScope} party={l.contractorName} />,
          <span style={{ color: '#6b7783' }}>{l.plannedWorkers ?? '-'}</span>,
          l.maleCount,
          l.femaleCount,
          l.skilledCount,
          l.semiSkilledCount,
          l.unskilledCount,
          <ActualAgainstPlan actual={l.totalWorkers ?? 0} planned={l.plannedWorkers} />,
          num(l.workingHours),
          num(l.overtimeHours),
          num(l.manHours),
          editable ? (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <IconButton size="small" onClick={() => setEditingRow(l)} title="Edit these numbers">
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" onClick={() => setConfirmRow(l)} title="Remove">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null,
        ])}
        empty="No labour recorded yet."
      />

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <LabourQuickForm
          categories={(categories.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
          contractors={(contractors.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
          pending={bulk.isPending}
          error={bulk.error}
          onCancel={() => setOpen(false)}
          onSave={async (rows) => {
            await bulk.mutateAsync(rows)
            setOpen(false)
          }}
        />
      </Dialog>

      <Dialog open={Boolean(editingRow)} onClose={() => setEditingRow(null)} fullWidth maxWidth="sm">
        {editingRow && (
          <LabourEditForm
            entry={editingRow}
            contractors={(contractors.data ?? []).map((c) => ({ id: c.id, label: c.name }))}
            pending={update.isPending}
            onCancel={() => setEditingRow(null)}
            onSave={async (body) => {
              await update.mutateAsync({ id: editingRow.id, body })
              setEditingRow(null)
            }}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmRow)}
        title="Remove this labour entry?"
        detail={`${confirmRow?.labourCategoryName ?? ''} — ${confirmRow?.totalWorkers ?? 0} workers come off today's headcount.`}
        pending={remove.isPending}
        onCancel={() => setConfirmRow(null)}
        onConfirm={async () => {
          if (confirmRow) await remove.mutateAsync(confirmRow.id)
          setConfirmRow(null)
        }}
      />
    </>
  )
}

/** One confirmation dialog for every delete on this page. */
function ConfirmDialog({
  open,
  title,
  detail,
  pending,
  onCancel,
  onConfirm,
}: {
  open: boolean
  title: string
  detail: string
  pending: boolean
  onCancel: () => void
  onConfirm: () => Promise<void> | void
}) {
  return (
    <Dialog open={open} onClose={onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <Button color="error" variant="contained" disabled={pending} onClick={() => void onConfirm()}>
          Remove
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// --------------------------------------------------------- material tab

function MaterialTab({ reportId, editable }: { reportId: string; editable: boolean }) {
  const list = materialReceiptApi.useList(reportId)
  const create = materialReceiptApi.useCreate(reportId)
  const update = materialReceiptApi.useUpdate(reportId)
  const remove = materialReceiptApi.useDelete(reportId)
  const [open, setOpen] = useState(false)
  const [editingReceipt, setEditingReceipt] = useState<MaterialReceipt | null>(null)
  const [confirmReceipt, setConfirmReceipt] = useState<MaterialReceipt | null>(null)

  return (
    <>
      <TemplateBar module="MATERIAL" reportId={reportId} editable={editable} />
      <Toolbar
        title="Deliveries received at site today, with challan and quantity split."
        action={
          editable && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              Add delivery
            </Button>
          )
        }
      />
      {create.error && <ErrorBlock error={create.error} title="Could not save the delivery" />}
      {update.error && <ErrorBlock error={update.error} title="Could not save the change" />}
      {remove.error && <ErrorBlock error={remove.error} title="Could not remove the delivery" />}
      {(list.data ?? []).map((m: MaterialReceipt) => (
        <Paper key={m.id} variant="outlined" sx={{ mb: 2, p: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 1 }}>
            <Typography sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{m.receiptNumber}</Typography>
            <ScopeChip scope={m.supplyScope} party={m.contractorName} />
            <Typography variant="body2">{m.supplierName ?? 'No supplier'}</Typography>
            {m.challanNumber && (
              <Typography variant="body2" color="text.secondary">
                challan {m.challanNumber}
              </Typography>
            )}
            {m.vehicleNumber && (
              <Typography variant="body2" color="text.secondary">
                vehicle {m.vehicleNumber}
              </Typography>
            )}
            <Box sx={{ flex: 1 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {m.totalAmount != null ? `₹ ${Number(m.totalAmount).toLocaleString('en-IN')}` : ''}
            </Typography>
            {editable && (
              <>
                <IconButton size="small" onClick={() => setEditingReceipt(m)} title="Edit this delivery">
                  <EditOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => setConfirmReceipt(m)} title="Remove">
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </>
            )}
          </Box>
          <DataTable
            columns={['Material', 'Unit', 'Ordered', 'Received', 'Accepted', 'Rejected', 'Balance', 'Rate', 'Amount']}
            rows={m.items.map((i) => [
              `${i.materialCode ?? ''} ${i.materialName ?? ''}`.trim(),
              i.uomCode,
              num(i.orderedQuantity),
              num(i.receivedQuantity),
              num(i.acceptedQuantity),
              Number(i.rejectedQuantity) > 0 ? <strong style={{ color: '#b3261e' }}>{num(i.rejectedQuantity)}</strong> : '0',
              num(i.balanceQuantity),
              i.rate != null ? num(i.rate) : '-',
              i.amount != null ? num(i.amount) : '-',
            ])}
            empty="No lines."
          />
        </Paper>
      ))}
      {(list.data?.length ?? 0) === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
          No deliveries recorded today.
        </Typography>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="lg">
        <MaterialMultiForm
          pending={create.isPending}
          error={create.error}
          onCancel={() => setOpen(false)}
          onSave={async (body) => {
            await create.mutateAsync(body)
            setOpen(false)
          }}
        />
      </Dialog>

      <Dialog
        open={Boolean(editingReceipt)}
        onClose={() => setEditingReceipt(null)}
        fullWidth
        maxWidth="lg"
      >
        {editingReceipt && (
          <MaterialMultiForm
            pending={update.isPending}
            error={update.error}
            initial={{
              receiptNumber: editingReceipt.receiptNumber,
              supplierId: editingReceipt.supplierId,
              supplyScope: editingReceipt.supplyScope,
              contractorId: editingReceipt.contractorId,
              challanNumber: editingReceipt.challanNumber,
              challanDate: editingReceipt.challanDate,
              vehicleNumber: editingReceipt.vehicleNumber,
              poNumber: editingReceipt.poNumber,
              items: editingReceipt.items.map((i) => ({
                materialId: i.materialId,
                orderedQuantity: i.orderedQuantity,
                receivedQuantity: i.receivedQuantity,
                acceptedQuantity: i.acceptedQuantity,
                rejectedQuantity: i.rejectedQuantity,
                rate: i.rate,
              })),
            }}
            onCancel={() => setEditingReceipt(null)}
            onSave={async (body) => {
              await update.mutateAsync({ id: editingReceipt.id, body })
              setEditingReceipt(null)
            }}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmReceipt)}
        title="Remove this delivery?"
        detail={`${confirmReceipt?.receiptNumber ?? ''} and all its material lines come off today's report and off stock.`}
        pending={remove.isPending}
        onCancel={() => setConfirmReceipt(null)}
        onConfirm={async () => {
          if (confirmReceipt) await remove.mutateAsync(confirmReceipt.id)
          setConfirmReceipt(null)
        }}
      />
    </>
  )
}

// ----------------------------------------------------------- issues tab

function IssuesTab({ reportId, siteId, editable }: { reportId: string; siteId: string; editable: boolean }) {
  const list = siteIssueApi.useList(reportId)
  const create = siteIssueApi.useCreate(reportId)
  const update = siteIssueApi.useUpdate(reportId)
  const remove = siteIssueApi.useDelete(reportId)
  const categories = useIssueCategories()
  const [open, setOpen] = useState(false)
  const [editingIssue, setEditingIssue] = useState<SiteIssue | null>(null)
  const [confirmIssue, setConfirmIssue] = useState<SiteIssue | null>(null)
  const emptyForm = {
    description: '',
    impact: '',
    priority: 'MEDIUM' as IssuePriority,
    issueCategoryId: '',
    responsibleParty: '',
    targetResolutionDate: '',
  }
  const [form, setForm] = useState(emptyForm)

  return (
    <>
      <Toolbar
        title="Anything holding up work: material, labour, drawings, approvals."
        action={
          editable && (
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={() => setOpen(true)}>
              Raise issue
            </Button>
          )
        }
      />
      {create.error && <ErrorBlock error={create.error} title="Could not raise the issue" />}
      {update.error && <ErrorBlock error={update.error} title="Could not save the change" />}
      {remove.error && <ErrorBlock error={remove.error} title="Could not remove the issue" />}
      <DataTable
        columns={['No.', 'Category', 'Description', 'Priority', 'Responsible', 'Target', 'Age', 'Status', '']}
        rows={(list.data ?? []).map((i: SiteIssue) => [
          <span style={{ fontFamily: 'monospace' }}>{i.issueNumber}</span>,
          i.categoryName ?? '-',
          truncate(i.description, 60),
          <PriorityChip priority={i.priority} />,
          i.responsibleParty ?? '-',
          i.targetResolutionDate ? formatDate(i.targetResolutionDate) : '-',
          <span style={{ color: i.overdue ? '#b3261e' : undefined, fontWeight: i.overdue ? 700 : 400 }}>
            {i.ageDays}d
          </span>,
          <IssueStatusChip status={i.status} />,
          editable ? (
            <Box sx={{ whiteSpace: 'nowrap' }}>
              <IconButton
                size="small"
                title="Edit this issue"
                onClick={() => {
                  setEditingIssue(i)
                  setForm({
                    description: i.description,
                    impact: i.impact ?? '',
                    priority: i.priority,
                    issueCategoryId: i.issueCategoryId ?? '',
                    responsibleParty: i.responsibleParty ?? '',
                    targetResolutionDate: i.targetResolutionDate ?? '',
                  })
                  setOpen(true)
                }}
              >
                <EditOutlinedIcon fontSize="small" />
              </IconButton>
              <IconButton size="small" color="error" title="Remove" onClick={() => setConfirmIssue(i)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null,
        ])}
        empty="No issues raised on this report."
      />

      <Dialog
        open={open}
        onClose={() => {
          setOpen(false)
          setEditingIssue(null)
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingIssue ? `Edit issue ${editingIssue.issueNumber}` : 'Raise a site issue'}
        </DialogTitle>
        <DialogContent dividers>
          <TextField label="Description" required fullWidth multiline minRows={3}
                     value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                     sx={{ mb: 2 }} />
          <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
            <TextField select label="Category" value={form.issueCategoryId}
                       onChange={(e) => setForm({ ...form, issueCategoryId: e.target.value })}>
              <MenuItem value="">Not categorised</MenuItem>
              {(categories.data ?? []).map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
            </TextField>
            <TextField select label="Priority" value={form.priority}
                       onChange={(e) => setForm({ ...form, priority: e.target.value as IssuePriority })}>
              {(['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'] as IssuePriority[]).map((p) => (
                <MenuItem key={p} value={p}>{p.toLowerCase()}</MenuItem>
              ))}
            </TextField>
            <TextField label="Responsible party" value={form.responsibleParty}
                       onChange={(e) => setForm({ ...form, responsibleParty: e.target.value })} />
            <TextField label="Target resolution" type="date" value={form.targetResolutionDate}
                       onChange={(e) => setForm({ ...form, targetResolutionDate: e.target.value })}
                       slotProps={{ inputLabel: { shrink: true } }} />
          </Box>
          <TextField label="Impact on work" fullWidth multiline minRows={2} sx={{ mt: 2 }}
                     value={form.impact} onChange={(e) => setForm({ ...form, impact: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpen(false)
              setEditingIssue(null)
              setForm(emptyForm)
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={create.isPending || update.isPending || form.description.trim().length < 5}
            onClick={async () => {
              const body = {
                siteId,
                description: form.description,
                impact: form.impact || undefined,
                priority: form.priority,
                issueCategoryId: form.issueCategoryId || undefined,
                responsibleParty: form.responsibleParty || undefined,
                targetResolutionDate: form.targetResolutionDate || undefined,
              }
              if (editingIssue) {
                await update.mutateAsync({ id: editingIssue.id, body })
              } else {
                await create.mutateAsync(body)
              }
              setOpen(false)
              setEditingIssue(null)
              setForm(emptyForm)
            }}
          >
            {create.isPending || update.isPending
              ? 'Saving...'
              : editingIssue
                ? 'Save changes'
                : 'Raise issue'}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={Boolean(confirmIssue)}
        title="Remove this issue?"
        detail={`Issue ${confirmIssue?.issueNumber ?? ''} is withdrawn from this report.`}
        pending={remove.isPending}
        onCancel={() => setConfirmIssue(null)}
        onConfirm={async () => {
          if (confirmIssue) await remove.mutateAsync(confirmIssue.id)
          setConfirmIssue(null)
        }}
      />
    </>
  )
}

// ---------------------------------------------------------- history tab

function HistoryTab({ reportId }: { reportId: string }) {
  const history = useReportHistory(reportId)
  if (history.isLoading) return <LoadingBlock />
  return (
    <DataTable
      columns={['When', 'Action', 'From', 'To', 'By', 'Remarks']}
      rows={(history.data ?? []).map((h) => [
        new Date(h.createdAt).toLocaleString(),
        <strong>{h.action}</strong>,
        h.fromStatus ?? '-',
        h.toStatus,
        h.actorName ?? '-',
        h.remarks ?? '-',
      ])}
      empty="No history."
    />
  )
}

// ------------------------------------------------------------- helpers

function RejectDialog({
  open,
  onClose,
  onReject,
}: {
  open: boolean
  onClose: () => void
  onReject: (reason: string) => Promise<void>
}) {
  const [reason, setReason] = useState('')
  const tooShort = reason.trim().length > 0 && reason.trim().length < 10
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Return this report for correction</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          The engineer sees this reason and can edit the report again. Be specific about what to fix.
        </Typography>
        <TextField
          label="Reason"
          fullWidth
          required
          multiline
          minRows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          error={tooShort}
          helperText={tooShort ? 'At least 10 characters' : ' '}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button color="error" variant="contained" disabled={reason.trim().length < 10}
                onClick={() => onReject(reason)}>
          Reject report
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function Toolbar({ title, action }: { title: string; action?: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
      <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
        {title}
      </Typography>
      {action}
    </Box>
  )
}

function DataTable({
  columns,
  rows,
  empty,
}: {
  columns: string[]
  rows: ReactNode[][]
  empty: string
}) {
  return (
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((c, i) => (
              <TableCell key={i}>{c}</TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((cells, r) => (
            <TableRow key={r} hover>
              {cells.map((cell, c) => (
                <TableCell key={c}>{cell}</TableCell>
              ))}
            </TableRow>
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={columns.length} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                {empty}
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {label}
      </Typography>
      <Tooltip title={value ?? ''}>
        <Typography variant="body2" noWrap>
          {value || '-'}
        </Typography>
      </Tooltip>
    </Box>
  )
}

function num(value?: number | null): string {
  if (value == null) return '-'
  return Number(value).toLocaleString(undefined, { maximumFractionDigits: 3 })
}

function truncate(text: string | undefined, max: number): string {
  if (!text) return '-'
  return text.length > max ? `${text.slice(0, max)}...` : text
}

function formatDate(value: string): string {
  const d = new Date(value)
  return Number.isNaN(d.getTime())
    ? value
    : d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
}
